import { useMemo } from "react";
import { useForm } from "react-hook-form";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { useYupValidationResolver } from "@ryde/redux";
import { errors, Log, RecaptchaEnterpriseActions } from "@ryde/shared";
import {
  apiDataFetchingLandingAtom,
  createActiveStepError,
  createMessage,
  HerzliyaLoginStepFormFieldsEnum,
  IHerzliyaLoginLandingModel,
  landingMessagesAtom,
  LandingStepEnum,
  LandingStepErrorsEnum,
  LoginStepFormFieldsEnum,
  useLandingActions,
  useLandingApiAuth,
  useLandingOtpErrors,
  useLandingRecaptchaToken,
} from "@ryde/utils-landings";

import { yupLoginLandingHerzliyaValidationSchema } from "./parts";

export const useStepLoginHerzliya = () => {
  const { goToStep, setActiveStepErrors } = useLandingActions();
  const { requestPublicOtp } = useLandingApiAuth();
  const { handleErrors } = useLandingOtpErrors();
  const { getRecaptchaToken } = useLandingRecaptchaToken();
  const setServerErrors = useSetRecoilState(landingMessagesAtom);
  const setApiLoading = useSetRecoilState(apiDataFetchingLandingAtom);

  const resolver = useYupValidationResolver(
    yupLoginLandingHerzliyaValidationSchema,
    "landing",
  );

  const methods = useForm<IHerzliyaLoginLandingModel>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      acceptTerms: false,
    },
    resolver,
  });

  const {
    getValues,
    clearErrors,
    trigger,
    watch,
    formState: { isValid },
  } = methods;

  const firstNameValue = watch(HerzliyaLoginStepFormFieldsEnum.FirstName);
  const lastNameValue = watch(HerzliyaLoginStepFormFieldsEnum.lastName);
  const phoneWatchValue = watch(LoginStepFormFieldsEnum.Phone);
  const acceptTermsValue = watch(HerzliyaLoginStepFormFieldsEnum.AcceptTerms);

  const isContinueCanBeApply = useMemo(() => {
    return (
      isValid &&
      firstNameValue &&
      lastNameValue &&
      phoneWatchValue &&
      acceptTermsValue
    );
  }, [
    firstNameValue,
    isValid,
    lastNameValue,
    phoneWatchValue,
    acceptTermsValue,
  ]);

  const handleLogin = useEvent(async (): Promise<void> => {
    const isValidAfterTrigger = await trigger();

    if (!isValidAfterTrigger) {
      return;
    }

    setApiLoading(true);
    setServerErrors(createMessage([]));
    const firstName = getValues(HerzliyaLoginStepFormFieldsEnum.FirstName);
    const lastName = getValues(HerzliyaLoginStepFormFieldsEnum.lastName);
    const phone = getValues(HerzliyaLoginStepFormFieldsEnum.Phone);
    const recaptchaToken = await getRecaptchaToken(
      RecaptchaEnterpriseActions.REQUEST_OTP,
    );

    try {
      const requestOtpResponse = await requestPublicOtp({
        recaptchaToken,
        phone,
      });

      const { success, error } = requestOtpResponse ?? {};

      if (success) {
        setServerErrors(createMessage([]));
        goToStep(LandingStepEnum.VerificationStep, {
          firstName,
          lastName,
          phone,
        });
      } else {
        handleErrors({
          error,
          defaultAction(e) {
            setServerErrors(createMessage([errors.auth.errorWhileRequestOTP]));
          },
        });
      }
    } catch (error) {
      Log.warn("Error while requesting OTP", error);
      setActiveStepErrors(
        createActiveStepError(LandingStepErrorsEnum.GeneralError),
      );
      goToStep(LandingStepEnum.ErrorStep);
    } finally {
      setApiLoading(false);
    }
  });

  return { methods, handleLogin, isContinueCanBeApply };
};
